/**
 * x-predictive-search directive.
 *
 * Provides live predictive search results using the Shopify Predictive Search
 * API. As the user types, the directive fetches matching results and morphs
 * them into the results container. When the search term is cleared, an empty
 * state section is loaded instead.
 *
 * @see https://shopify.dev/docs/api/ajax/reference/predictive-search
 *
 * @options
 *   sectionId      - The Shopify section ID for the results markup.
 *   emptySectionId - The Shopify section ID for the empty/default state markup.
 *   searchUrl      - The predictive search API endpoint (e.g. `/search/suggest`).
 *
 * @example
 *   <div x-predictive-search="{ sectionId: 'predictive-search', emptySectionId: 'predictive-search-empty', searchUrl: '/search/suggest' }">
 *     <input x-model="searchTerm" type="search">
 *     <div x-ref="predictiveSearchResults">
 *       <div class="predictive-search-results">…</div>
 *     </div>
 *   </div>
 */
(() => {
  /** @type {string} Abort key used to cancel in-flight predictive search requests. */
  const PredictiveSearchAbortKey = "predictive-search";

  const root = (Alpine, el, { sectionId, emptySectionId, searchUrl }) => {
    Alpine.bind(el, () => {
      return {
        ["x-data"]() {
          return {
            searchTerm: "",
            hasResults: false,
            loading: false,

            /**
             * Watches `searchTerm` and triggers a search reset when it becomes empty.
             */
            init() {
              this.$watch("searchTerm", () => {
                this.reactiveSearch();
              });
            },

            /**
             * Resets the search results when the search term is cleared.
             */
            reactiveSearch() {
              if (!this.searchTerm.length) {
                this.resetSearch();
              }
            },

            /**
             * Extracts the `.predictive-search-results` element from an HTML string.
             * @param {string} html - The raw HTML text response.
             * @returns {Element|null} The results element, or null if not found.
             */
            parseSectionMarkup(html) {
              const markup = new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector(".predictive-search-results");

              return markup;
            },

            /**
             * Fetches predictive search results for the current `searchTerm` and
             * morphs them into the results container. No-ops when the term is empty.
             * @returns {Promise<void>}
             */
            async getSearchResults() {
              if (!this.searchTerm.length) {
                return;
              }

              const url = new URL(searchUrl, location.origin);

              url.searchParams.set("q", this.searchTerm);
              url.searchParams.set("resources[limit_scope]", "each");
              url.searchParams.set("section_id", sectionId);

              const predictiveSearchResults = this.$root.querySelector(
                '[x-ref="predictiveSearchResults"] > .predictive-search-results',
              );

              this.loading = true;

              await this.$app.$prefetchManager
                .fetchLatest(url.toString(), PredictiveSearchAbortKey, {
                  noCache: true,
                })
                .then((result) => result.text())
                .then((html) => {
                  const resultsMarkup = this.parseSectionMarkup(html);

                  if (!resultsMarkup) {
                    throw new Error("No section markup found.");
                  }

                  this.$morph(predictiveSearchResults, resultsMarkup);
                })
                .catch((error) => {
                  if (error.name === "AbortError") {
                    console.warn("Fetch aborted by user.");
                  } else {
                    console.error(error);
                  }
                })
                .finally(() => {
                  this.loading = false;

                  this.hasResults = !!this.$root.querySelector(
                    ".predictive-search-results-products",
                  );
                });
            },

            /**
             * Loads the empty state section and morphs it into the results container.
             * Called automatically when the search term is cleared.
             * @returns {Promise<void>}
             */
            async resetSearch() {
              const predictiveSearchResults = this.$root.querySelector(
                '[x-ref="predictiveSearchResults"] > .predictive-search-results',
              );

              const url = new URL(window.location.href);

              url.searchParams.delete("page");
              url.searchParams.set("section_id", emptySectionId);

              await this.$app.$prefetchManager
                .fetchLatest(url.toString(), PredictiveSearchAbortKey, {
                  noCache: true,
                })
                .then((response) => response.text())
                .then((html) => {
                  const parsedEmptySectionMarkup =
                    this.parseSectionMarkup(html);

                  if (!parsedEmptySectionMarkup) {
                    throw new Error("No empty section markup found.");
                  }

                  this.$morph(
                    predictiveSearchResults,
                    parsedEmptySectionMarkup,
                  );
                })
                .catch((error) => {
                  if (error.name === "AbortError") {
                    console.warn("Fetch aborted by user.");
                  } else {
                    console.error(error);
                  }
                })
                .finally(() => {
                  this.hasResults = !!this.$root.querySelector(
                    ".predictive-search-results-products",
                  );
                });
            },
          };
        },
      };
    });
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "predictive-search",
      (el, { expression }, { evaluate }) => {
        root(Alpine, el, expression ? evaluate(expression) : {});
      },
    );
  });
})();
